import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import { Report } from 'notiflix/build/notiflix-report-aio'

import { titleData } from '../../../data/titles'
import { contactPreferencesData } from '../../../data/contactPreferences'
import {
    InputFieldText,
    InputFieldSelect,
    FieldGroup,
    Field,
    SubmitButton,
    ButtonContainer,
    FieldContainer,
    FormCard,
} from '../../reusable'

import { FormPartInterface, SaveValueInterface } from '../../../interfaces'
import { Checkbox } from '../../reusable/checkbox'
import { InputFieldAddressLookup } from '../../reusable/inputFieldAddressLookup'
import { sectionValidation } from '../../../helpers'
import { getWidth } from '../../../helpers/screenWidth'
import { trackLead } from '../../../helpers/handleTracking'
import { IsMobile } from '../../layout'

export const AddressDetails = (props: FormPartInterface) => {
    const { saveValueFunct, isActive, assignGroup, validateSection } = props
    const [currentStep, setCurrentStep] = useState(0)
    const [postCurrentStep, setPostCurrentStep] = useState(0)
    const [stepData, setStepData] = useState<any>({})
    const [showErrors, setShowErrors] = useState(false)

    const [AddressText, setAddressText] = useState('')
    const [AddressOne, setAddressOne] = useState('')
    const [AddressTwo, setAddressTwo] = useState('')
    const [AddressThree, setAddressThree] = useState('')
    const [AddressTown, setAddressTown] = useState('')
    const [AddressCounty, setAddressCounty] = useState('')
    const [AddressPostcode, setAddressPostcode] = useState('')

    const width = getWidth()
    const height = Number(width / 1.8).toFixed(0)

    const preSaveValueFunct = (dataToSave: SaveValueInterface) => {
        if (dataToSave.field === 'searchAddress') {
            saveAddress(dataToSave.value)
            return { success: true, error: '' }
        }

        switch (dataToSave.field) {
            case 'address_line_one':
                setAddressOne(dataToSave.value)
                break
            case 'address_line_two':
                setAddressTwo(dataToSave.value)
                break
            case 'address_line_three':
                setAddressThree(dataToSave.value)
                break
            case 'city':
                setAddressTown(dataToSave.value)
                break
            case 'county':
                setAddressCounty(dataToSave.value)
                break
            case 'postcode':
                setAddressPostcode(dataToSave.value)
                break
            default:
                break
        }

        const saved = saveValueFunct(dataToSave)
        setStepData((prevData: Record<string, any>) => ({
            ...prevData,
            [dataToSave.field]: dataToSave.value,
        }))
        return saved
    }

    const saveAddress = (addressString: string) => {
        setAddressText('')
        setAddressOne('')
        setAddressTwo('')
        setAddressThree('')
        setAddressTown('')
        setAddressCounty('')
        setAddressPostcode('')

        saveValueFunct({ field: 'address_line_one', value: '' })
        saveValueFunct({ field: 'address_line_two', value: '' })
        saveValueFunct({ field: 'address_line_three', value: '' })
        saveValueFunct({ field: 'city', value: '' })
        saveValueFunct({ field: 'county', value: '' })
        saveValueFunct({ field: 'postcode', value: '' })

        setAddressText(addressString)
        const addressArr = addressString.split(', ')

        const lineOne = addressArr[0] || ''
        const lineTwo = addressArr.length > 3 ? addressArr[1] || '' : ''
        const lineThree = addressArr.length > 4 ? addressArr[addressArr.length - 3] || '' : ''
        const town = addressArr[addressArr.length - 2] || ''
        const county = addressArr[addressArr.length - 1] || ''
        const postcode = addressArr[addressArr.length - 1] || ''

        setAddressOne(lineOne)
        saveValueFunct({ field: 'address_line_one', value: lineOne })

        setAddressTwo(lineTwo)
        saveValueFunct({ field: 'address_line_two', value: lineTwo })

        setAddressThree(lineThree)
        saveValueFunct({ field: 'address_line_three', value: lineThree })

        setAddressTown(town)
        saveValueFunct({ field: 'city', value: town })

        setAddressCounty(county)
        saveValueFunct({ field: 'county', value: county })

        setAddressPostcode(postcode)
        saveValueFunct({ field: 'postcode', value: postcode })

        setStepData((prevData: Record<string, any>) => ({
            ...prevData,
            address_line_one: lineOne,
            address_line_two: lineTwo,
            address_line_three: lineThree,
            city: town,
            county: county,
            postcode: postcode,
        }))
    }

    const animateBeforeAssign = (assignment: string) => {
        setShowErrors(true)
        if (!sectionValidation(stepData, 'address').success) return
        setTimeout(() => {
            assignGroup(assignment)
        }, 400)
    }

    useEffect(() => {}, [])

    const getCurrentStep = () => {
        return isActive === 'address' ? (
            <FieldGroup title="Address Information">
                <FormCard assignPostStep={setPostCurrentStep} step={currentStep}>
                    <h3>Your Address</h3>
                    <p className={styles.description}>
                        Please enter your current address so we can send you important documents regarding your claim.
                    </p>
                    <Field extraStyle={{ padding: '0' }} fieldLabel="Search Address" labelPosition="left">
                        <InputFieldAddressLookup
                            fieldName={`searchAddress`}
                            saveValue={preSaveValueFunct}
                            enhanced={true}
                        />
                    </Field>
                    {AddressText.length > 0 && (
                        <>
                            <Field
                                extraStyle={{ padding: '0' }}
                                fieldLabel="Address Line 1"
                                labelPosition="left"
                                enhanced={true}
                            >
                                <InputFieldText
                                    fieldName="address_line_one"
                                    saveValue={preSaveValueFunct}
                                    populateValue={AddressOne}
                                    enhanced={true}
                                    required={true}
                                    showErrors={showErrors}
                                />
                            </Field>
                            <Field
                                extraStyle={{ padding: '0' }}
                                fieldLabel="Address Line 2"
                                labelPosition="left"
                                enhanced={true}
                            >
                                <InputFieldText
                                    fieldName="address_line_two"
                                    saveValue={preSaveValueFunct}
                                    populateValue={AddressTwo}
                                    enhanced={true}
                                />
                            </Field>
                            <Field
                                extraStyle={{ padding: '0' }}
                                fieldLabel="Address Line 3"
                                labelPosition="left"
                                enhanced={true}
                            >
                                <InputFieldText
                                    fieldName="address_line_three"
                                    saveValue={preSaveValueFunct}
                                    populateValue={AddressThree}
                                    enhanced={true}
                                />
                            </Field>
                            <Field extraStyle={{ padding: '0' }} fieldLabel="City" labelPosition="left" enhanced={true}>
                                <InputFieldText
                                    fieldName="city"
                                    saveValue={preSaveValueFunct}
                                    populateValue={AddressTown}
                                    enhanced={true}
                                    required={true}
                                    showErrors={showErrors}
                                />
                            </Field>
                            <Field
                                extraStyle={{ padding: '0' }}
                                fieldLabel="Postcode"
                                labelPosition="left"
                                enhanced={true}
                            >
                                <InputFieldText
                                    fieldName="postcode"
                                    saveValue={preSaveValueFunct}
                                    populateValue={AddressPostcode}
                                    enhanced={true}
                                    required={true}
                                    showErrors={showErrors}
                                />
                            </Field>
                        </>
                    )}
                    <SubmitButton text="Continue" btnType="primary" onClick={() => animateBeforeAssign('person')} />
                </FormCard>
            </FieldGroup>
        ) : (
            <></>
        )
    }

    return getCurrentStep()
}
